import { Stack, Typography, Paper, Box } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Authentication } from '../../auth/Authentication.types';
import { verifyUserMFA, extractAuthentication } from '../../auth/authenticator';
import { getUserInfo } from '../../networking/userInfo/userInfo';
import { UserInfo } from '../../common/interfaces/UserInfo.types';
import { AuthContext } from '../../common/authContext';

import ViewState from '../SetupMFA/ViewState';
import ValidateMFA from '../SetupMFA/ValidateMFA';
import ContentBody from '../../components/ContentBody/ContentBody';
import UserAuthentication from './UserAuthentication';
import Session from '../../common/Session.types';
import { ACCOUNT_DETAILS_ACCOUNTS_PAGE, ROUTES } from '../../routing/ROUTES';
import { RoutesEnum } from '../../routing/types';
import { useNavigate } from 'react-router-dom';
import MFALoginAction, { getMFALoginAction } from '../../utils/login/mfaLoginAction';
import { CognitoUser } from '@aws-amplify/auth';
import { RoutingHistoryContext } from '../../common/routingHistoryContext';
import { MFASetupContext } from '../../common/mfaSetupContext';

const Login = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [viewState, setViewState] = useState(ViewState.SignIn);
  const { setAuthState, setUserInfo, setSession } = useContext(AuthContext);
  const { setCredentials: setMFASetupCredentials } = useContext(MFASetupContext);
  const navigate = useNavigate();
  const { landingURL, isLandingURLValid } = useContext(RoutingHistoryContext);

  const login = (userInfo: UserInfo): void => {
    setUserInfo(userInfo);

    if (userInfo.role == 'Client' && userInfo.myClients.length == 1) {
      setSession(Session.Client);
      navigate(isLandingURLValid() ? (landingURL as RoutesEnum) : ACCOUNT_DETAILS_ACCOUNTS_PAGE);
    } else if (userInfo.role == 'Admin' || userInfo.role == 'User' || (userInfo.role == 'Client' && userInfo.myClients.length > 1)) {
      setSession(Session.RelationshipManager);
      navigate(ROUTES.RelationshipManager);
    }
  };

  const onMFAComplete = async (value: string): Promise<void> => {
    const authentication: Authentication = await verifyUserMFA(user, value);
    if (authentication.isSuccess) {
      try {
        const userInfo = await getUserInfo(authentication.bearerToken);
        setAuthState({
          clientId: userInfo.allClients[0].id,
          clientName: userInfo.allClients[0].name,
          elevatedUser: userInfo.role != 'Client',
          bearerToken: authentication.bearerToken
        });
        login(userInfo);
      } catch (error) {
        console.log('ERROR - Unable to login to cashVest');
      }
    } else {
      console.error('ERROR - Unable to authenticate user: ', authentication.error);
      throw authentication.error;
    }
  };

  const completeLogin = async (cognitoUser: CognitoUser): Promise<void> => {
    const authentication = extractAuthentication(cognitoUser);
    if (authentication.isSuccess) {
      try {
        const userInfo = await getUserInfo(authentication.bearerToken);
        setAuthState({
          clientId: userInfo.allClients[0].id,
          clientName: userInfo.allClients[0].name,
          elevatedUser: userInfo.role != 'Client',
          bearerToken: authentication.bearerToken
        });
        login(userInfo);
      } catch (error) {
        console.log('ERROR - Unable to login to cashVest');
      }
    } else {
      console.error('ERROR - Unable to authenticate user: ', authentication.error);
    }
  };

  const onAuthenticationComplete = async (
    cognitoUser: CognitoUser,
    username: string,
    password: string
  ) => {
    setUser(cognitoUser);

    const mfaAction = getMFALoginAction(cognitoUser.challengeName);
    if (mfaAction === MFALoginAction.USE_MFA) {
      setViewState(ViewState.VerifyMFA);
    } else if (mfaAction === MFALoginAction.SETUP_MFA) {
      // Hand credentials to the SetupMFA page via in-memory context
      // instead of URL path params so they are never exposed in
      // browser history, logs, referrers, or telemetry.
      setMFASetupCredentials({ username, password });
      navigate(ROUTES.SetupMFA);
    } else if (mfaAction === MFALoginAction.NOT_AWS_PRODUCTION_ENVIRONMENT_SKIP_MFA) {
      await completeLogin(cognitoUser);
    }
  };

  const handleOnBack = () => {
    setViewState(ViewState.SignIn);
  };

  return (
    <ContentBody>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 5 }}>
        <Paper elevation={1} sx={{ backgroundColor: '#ffffff', width: '100%', maxWidth: '650px', p: 6, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography
              sx={{
                fontSize: '45px',
                color: 'primary.main',
                textAlign: 'center'
              }}
            >
              {t('login.loginTitle')}
            </Typography>

            <Typography
              sx={{
                fontSize: '18px',
                color: 'text.primary',
                textAlign: 'center',
                mb: 3
              }}
            >
              {t('login.welcomeMessage')}
            </Typography>

            {viewState === ViewState.SignIn && (
              <UserAuthentication onComplete={onAuthenticationComplete} />
            )}
            {viewState === ViewState.VerifyMFA && (
              <ValidateMFA onComplete={onMFAComplete} onBack={handleOnBack} />
            )}
          </Stack>
        </Paper>
      </Box>
    </ContentBody>
  );
};

export default Login;
